import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type { ComponentPropsWithoutRef, ElementRef } from "react";
import { forwardRef } from "react";
import { cn } from "../../helpers/styles";

// ----------------------------------------------------------------------------------------------

export const Card = forwardRef<
	ElementRef<"article">,
	ComponentPropsWithoutRef<"article"> & VariantProps<typeof cardVariants>
>(({ children, className, variant, ...props }, ref) => {
	return (
		<article ref={ref} className={cn(cardVariants({ variant }), className)} {...props}>
			{children}
		</article>
	);
});

Card.displayName = "Card";

const cardVariants = cva(
	"border-1 flex h-full flex-col rounded-xs border-gray-500 bg-gray-50 overflow-hidden md:min-w-[320px]",
	{
		variants: {
			variant: {
				boxed: "shadow-1",
			},
		},
	},
);

// ----------------------------------------------------------------------------------------------

export const CardMedia = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
	({ children, className, ...props }, ref) => {
		return (
			<div ref={ref} className={cn("w-full", className)} {...props}>
				{children}
			</div>
		);
	},
);

CardMedia.displayName = "CardMedia";

// ----------------------------------------------------------------------------------------------

export const CardContent = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
	({ children, className, ...props }, ref) => {
		return (
			<div
				ref={ref}
				className={cn("flex w-full flex-col items-start gap-3xl px-3xl pb-3xl pt-2xl", className)}
				{...props}
			>
				{children}
			</div>
		);
	},
);

CardContent.displayName = "CardContent";

// ----------------------------------------------------------------------------------------------

export const CardHeading = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
	({ children, className, ...props }, ref) => {
		return (
			<div ref={ref} className={cn("flex flex-col gap-md w-full", className)} {...props}>
				{children}
			</div>
		);
	},
);

CardHeading.displayName = "CardHeading";

// ----------------------------------------------------------------------------------------------

export const CardCategory = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
	({ children, className, ...props }, ref) => {
		return (
			<div ref={ref} className={cn("text-sm font-semibold text-brand-700", className)} {...props}>
				{children}
			</div>
		);
	},
);

CardCategory.displayName = "CardCategory";

// ----------------------------------------------------------------------------------------------

export const CardHeadline = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
	({ children, className, ...props }, ref) => {
		return (
			<div ref={ref} className={cn("flex flex-col gap-xs", className)} {...props}>
				{children}
			</div>
		);
	},
);

CardHeadline.displayName = "CardHeadline";

// ----------------------------------------------------------------------------------------------

export const CardTitleWrapper = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
	({ children, className, ...props }, ref) => {
		return (
			<div
				ref={ref}
				className={cn("flex w-full items-center justify-between gap-xl", className)}
				{...props}
			>
				{children}
			</div>
		);
	},
);

CardTitleWrapper.displayName = "CardTitleWrapper";

// ----------------------------------------------------------------------------------------------

export const CardTitle = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
	({ children, className, ...props }, ref) => {
		return (
			<div ref={ref} className={cn("text-xl font-semibold lg:display-xs", className)} {...props}>
				{children}
			</div>
		);
	},
);

CardTitle.displayName = "CardTitle";

// ----------------------------------------------------------------------------------------------

export const CardTitleIcon = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
	({ children, className, ...props }, ref) => {
		return (
			<div ref={ref} className={cn("flex items-center justify-center", className)} {...props}>
				{children}
			</div>
		);
	},
);

CardTitleIcon.displayName = "CardTitleIcon";

// ----------------------------------------------------------------------------------------------

export const CardSubtitle = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
	({ children, className, ...props }, ref) => {
		return (
			<div ref={ref} className={cn("truncate text-md text-gray-600", className)} {...props}>
				{children}
			</div>
		);
	},
);

CardSubtitle.displayName = "CardSubtitle";

// ----------------------------------------------------------------------------------------------

export const CardActions = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
	({ children, className, ...props }, ref) => {
		return (
			<div ref={ref} className={cn("flex gap-lg", className)} {...props}>
				{children}
			</div>
		);
	},
);

CardActions.displayName = "CardActions";

// ----------------------------------------------------------------------------------------------

export const CardFooter = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
	({ className, ...props }, ref) => (
		<div
			ref={ref}
			className={cn("flex items-center p-6 px-3 pt-0 md:px-6", className)}
			{...props}
		/>
	),
);
CardFooter.displayName = "CardFooter";

// ----------------------------------------------------------------------------------------------

export const CardDescription = forwardRef<ElementRef<"p">, ComponentPropsWithoutRef<"p">>(
	({ className, ...props }, ref) => (
		<p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
	),
);
CardDescription.displayName = "CardDescription";
